import {
  Box,
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useRef } from "react";
import { ProcessingStatusData, SourceDataRecord } from "../types";
import SDRCard from "./SDRCard";

interface SDRCardListProps {
  records: SourceDataRecord[];
  isDocumentDataset: boolean;
  onRowClick: (record: SourceDataRecord) => void;
  /** Whether more data is being fetched */
  isFetching: boolean;
  /** Whether there are more records to load */
  hasNextPage: boolean;
  /** Function to fetch the next page */
  fetchNextPage: () => void;
  /** Card variant */
  variant?: "compact" | "comfortable";
  /** Total record count for display */
  totalCount?: number;
  /** Number of columns for grid layout (1 = list, 2+ = grid) */
  columns?: number;
  /** Processing status map for showing detailed status per record */
  processingStatusMap?: Map<number, ProcessingStatusData>;
}

const SDRCardList = ({
  records,
  isDocumentDataset,
  onRowClick,
  isFetching,
  hasNextPage,
  fetchNextPage,
  variant = "comfortable",
  totalCount,
  columns = 1,
  processingStatusMap,
}: SDRCardListProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Infinite scroll - observe last card
  const lastCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetching) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 },
      );

      if (node) {
        observerRef.current.observe(node);
      }
    },
    [isFetching, hasNextPage, fetchNextPage],
  );

  // Loading skeletons
  const CardSkeleton = () => (
    <Box
      sx={{
        borderRadius: 2,
        bgcolor: "background.paper",
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        display: "flex",
        alignItems: "stretch",
        overflow: "hidden",
        height: variant === "compact" ? 110 : 160,
      }}
    >
      {isDocumentDataset && (
        <Skeleton
          variant="rectangular"
          width={variant === "compact" ? 80 : 120}
          height="100%"
        />
      )}
      <Box sx={{ flex: 1, p: 2 }}>
        <Skeleton variant="text" width="70%" height={24} />
        <Skeleton variant="text" width="90%" height={20} />
        <Skeleton variant="text" width="50%" height={16} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="30%" height={16} />
      </Box>
    </Box>
  );

  // Empty state
  if (records.length === 0 && !isFetching) {
    return (
      <Box
        data-testid="sdr-empty-state"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No records found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your search or filters
        </Typography>
      </Box>
    );
  }

  // Initial loading - show grid of skeletons
  if (records.length === 0 && isFetching) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} md={columns > 1 ? 6 : 12} key={i}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box
      data-testid="sdr-card-list"
      sx={{
        p: 2,
        overflowY: "auto",
        height: "100%",
      }}
    >
      {/* Record count */}
      {totalCount !== undefined && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          {totalCount.toLocaleString()} record{totalCount !== 1 ? "s" : ""}
        </Typography>
      )}

      {/* Card grid */}
      <Grid container spacing={2}>
        {records.map((record, index) => {
          const isLast = index === records.length - 1;
          return (
            <Grid
              item
              xs={12}
              md={columns > 1 ? 6 : 12}
              lg={columns > 2 ? 4 : columns > 1 ? 6 : 12}
              key={record.id}
              ref={isLast ? lastCardRef : undefined}
            >
              <SDRCard
                record={record}
                onRowClick={onRowClick}
                isDocumentDataset={isDocumentDataset}
                variant={variant}
                processingStatus={processingStatusMap?.get(record.id)}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Loading more indicator */}
      {isFetching && records.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ py: 2 }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading more...
          </Typography>
        </Stack>
      )}

      {/* End of list */}
      {!hasNextPage && records.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", py: 2 }}
        >
          End of results
        </Typography>
      )}
    </Box>
  );
};

export default SDRCardList;
